import { useMutation } from '@tanstack/react-query';

import {
    FidelityAccountList,
    FidelityApplicationSection,
    FidelityDownloadButton,
    FidelityDownloadFeedback,
    FidelityPacketCard,
} from '@/components/fidelity/FidelityApplicationComponents';
import { CustomerApi } from 'API';

import { accountTitle } from './account-model';
import { FidelitySmaSectionProps } from './useFidelitySmaApplications';

export const FidelityPersonalApplicationView = ({
    household,
    applications,
    action,
    ssnAccess,
    checkingAccess,
}: FidelitySmaSectionProps<'personal'>) => {
    const accountGUIDs = household.accounts.map(({ account }) => account.accountGUID);

    const generate = useMutation({
        mutationFn: (guids: string[]) => CustomerApi({ hideError: true }).getSmaPersonalForm({ accountGUIDs: guids }),
    });
    const title = (guid: string) => {
        const row = household.accounts.find(({ account }) => account.accountGUID === guid);
        return row ? accountTitle(row.account) : 'Account no longer available';
    };

    return (
        <FidelityApplicationSection
            name="Personal"
            hasAccounts={accountGUIDs.length > 0}
            groupCount={applications.data?.applications.length}
            checkingAccess={checkingAccess}
            ssnAccess={!!ssnAccess}
            applications={applications}
        >
            <>
                {applications.data?.applications.map((guids) => (
                    <FidelityPacketCard
                        key={guids.join(',')}
                        name="Personal"
                        accountCount={guids.length}
                        action={
                            <FidelityDownloadButton
                                download={generate}
                                variant="outline"
                                active={generate.variables === guids}
                                onClick={() => generate.mutate(guids)}
                            >
                                Generate SMA Personal Application
                            </FidelityDownloadButton>
                        }
                    >
                        <FidelityAccountList action={action} accountGUIDs={guids} title={title}>
                            {(guid) => (
                                <>
                                    {applications.data?.completionRequirements
                                        .filter((notice) => notice.accountGUID === guid)
                                        .map((notice) => (
                                            <p key={notice.reason} className="text-cocoa-900 mt-2 text-sm font-medium">
                                                Completion required: {notice.reason}
                                            </p>
                                        ))}
                                </>
                            )}
                        </FidelityAccountList>
                    </FidelityPacketCard>
                ))}
            </>
            <FidelityDownloadFeedback
                download={generate}
                onRefresh={() => {
                    generate.reset();
                    void applications.refetch();
                }}
            />
        </FidelityApplicationSection>
    );
};
